/**
 * SendBriefModal (Iter 36.c) — email the Workspace Risk Brief to a teammate
 * or an external stakeholder.
 *
 * Two recipient surfaces side-by-side:
 *   1. Workspace-member quick-pick (canonical when present — fewer typos).
 *   2. Free-form email input for external addresses (client PM, sponsor).
 *
 * Both modes feed the same `POST /workspaces/{ws}/risk-brief/email`
 * endpoint; the backend resolves either ``recipient_user_id`` or
 * ``recipient_email`` into a single ``to:`` address and renders the brief
 * server-side via ``email_service.risk_brief_email_html``.
 *
 * UX rules:
 *   - The teammate dropdown is collapsed by default but auto-opens when the
 *     consultant types nothing in the free-form input — that's the path we
 *     want them to land on first.
 *   - The free-form email input wins only when no member is selected.
 *   - Optional personal note rendered as a callout above the brief tables.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  XIcon as X,
  EnvelopeSimpleIcon as Envelope,
  CircleNotchIcon as Spinner,
  UserIcon as User,
} from "@phosphor-icons/react";
import {
  listWorkspaceMembers,
  sendRiskBriefEmail,
} from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

const MAX_MESSAGE = 1000;
// Lightweight email shape check — backend re-validates with EmailStr.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SendBriefModal({
  workspaceId,
  workspaceName,
  clientName,
  open,
  onClose,
  onSent,
}) {
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fresh state every open — different workspace = different members list.
  useEffect(() => {
    if (!open) return;
    setSelectedUserId("");
    setEmailInput("");
    setMessage("");
    setSubmitting(false);
    setLoadingMembers(true);
    listWorkspaceMembers(workspaceId)
      .then((res) => {
        setMembers((res.members || []).filter((m) => m.user_id));
      })
      .catch(() => setMembers([]))
      .finally(() => setLoadingMembers(false));
  }, [open, workspaceId]);

  const trimmedEmail = emailInput.trim().toLowerCase();
  const emailLooksValid = useMemo(
    () => !trimmedEmail || EMAIL_RE.test(trimmedEmail),
    [trimmedEmail],
  );

  // Member quick-pick wins over free-form email when both are present.
  const canSend =
    !submitting &&
    (selectedUserId || (trimmedEmail && emailLooksValid));

  const handleSend = async () => {
    if (!canSend) return;
    setSubmitting(true);
    try {
      const res = await sendRiskBriefEmail(workspaceId, {
        recipientUserId: selectedUserId || undefined,
        recipientEmail: !selectedUserId && trimmedEmail ? trimmedEmail : undefined,
        personalMessage: message.trim() || undefined,
      });
      const dest = res.recipient_email || "the recipient";
      toast.success(
        res.stubbed
          ? `Brief queued to ${dest} (stub mode — check backend logs).`
          : `Risk brief sent to ${dest}.`,
      );
      onSent?.(res);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to send the brief.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const title = clientName || workspaceName;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="send-brief-modal-backdrop"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white border border-[var(--ml-border)] max-w-lg w-full shadow-xl"
        data-testid="send-brief-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--ml-border)]">
          <div className="flex items-center gap-2">
            <Envelope size={16} weight="bold" className="text-[var(--ml-primary)]" />
            <h3 className="font-semibold text-lg">Send brief by email</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="send-brief-modal-close"
            className="ml-btn-icon text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm space-y-1">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Brief</div>
            <div className="font-semibold" data-testid="send-brief-modal-title">{title}</div>
            {clientName && clientName !== workspaceName && (
              <div className="text-xs text-[var(--ml-text-secondary)]">{workspaceName}</div>
            )}
          </div>

          {/* ---- Workspace-member quick-pick -------------------------- */}
          <div>
            <label
              htmlFor="send-brief-member"
              className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1 flex items-center gap-1"
            >
              <User size={11} weight="bold" /> Workspace member
            </label>
            <select
              id="send-brief-member"
              value={selectedUserId}
              onChange={(e) => {
                setSelectedUserId(e.target.value);
                if (e.target.value) setEmailInput("");
              }}
              disabled={loadingMembers || submitting}
              className="ml-input w-full text-sm"
              data-testid="send-brief-modal-member-select"
            >
              <option value="">
                {loadingMembers
                  ? "Loading members…"
                  : members.length === 0
                  ? "No teammates on this workspace yet"
                  : "— choose a teammate —"}
              </option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.name} · {m.email} ({m.role})
                </option>
              ))}
            </select>
          </div>

          {/* ---- OR free-form email ----------------------------------- */}
          <div className="relative">
            <div className="absolute left-0 right-0 -top-2 flex items-center justify-center">
              <span className="bg-white px-2 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                or
              </span>
            </div>
            <div className="border-t border-[var(--ml-border)] pt-3">
              <label
                htmlFor="send-brief-email"
                className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1 flex items-center gap-1"
              >
                <Envelope size={11} weight="bold" /> External email
              </label>
              <input
                id="send-brief-email"
                type="email"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  if (e.target.value) setSelectedUserId("");
                }}
                placeholder="client.pm@example.com"
                disabled={submitting}
                className="ml-input w-full text-sm"
                data-testid="send-brief-modal-email-input"
              />
              {!emailLooksValid && (
                <div className="text-[11px] text-[#a2191f] mt-1" data-testid="send-brief-modal-email-error">
                  That doesn&rsquo;t look like a valid email.
                </div>
              )}
            </div>
          </div>

          {/* ---- Optional personal note ------------------------------- */}
          <div>
            <label
              htmlFor="send-brief-note"
              className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1"
            >
              Personal note <span className="opacity-50 ml-mono">(optional, max {MAX_MESSAGE} chars)</span>
            </label>
            <textarea
              id="send-brief-note"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
              placeholder="Hi Sam — quick brief on where to focus first. Happy to walk you through the top packages on Thursday."
              rows={4}
              disabled={submitting}
              className="ml-input w-full text-sm"
              data-testid="send-brief-modal-note"
            />
            <div className="text-right text-[10px] ml-mono text-[var(--ml-text-secondary)] mt-0.5">
              {message.length} / {MAX_MESSAGE}
            </div>
          </div>

          <div className="text-xs text-[var(--ml-text-secondary)] ml-card p-3 bg-[var(--ml-surface)]/40">
            The brief renders the same SAP packages + IBM-i files tables shown
            on this page. Reply-To is set to your address, so replies land in
            your inbox.
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--ml-border)] -mx-5 px-5 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="ml-btn-secondary"
              data-testid="send-brief-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              data-testid="send-brief-modal-send"
              className="ml-btn-primary"
              title={
                selectedUserId
                  ? "Send to selected teammate"
                  : trimmedEmail
                  ? `Send to ${trimmedEmail}`
                  : "Choose a teammate or enter an email"
              }
            >
              {submitting ? (
                <><Spinner size={12} className="animate-spin" /> Sending…</>
              ) : (
                <><Envelope size={12} weight="bold" /> Send brief</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
